import { useState, useCallback, useRef, useEffect, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  SidebarSimple,
  Eye,
  EyeSlash,
  Check,
  ClockCounterClockwise,
  ShareNetwork,
  DotsThreeVertical,
  DownloadSimple,
  FileText,
  Printer,
  Keyboard,
  Gear,
  TextAa,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { APP_NAME, LeverMark, appNameStyle } from "../../lib/brand";
import { useUIStore } from "../../stores/ui";
import { AgentStatusIndicator } from "../ai/AgentStatus";
import { ModelSelector } from "../ai/ModelSelector";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownSeparator,
} from "../ui/Dropdown";

type DocumentType = "latex" | "markdown" | "docx";

interface TopBarProps {
  documentTitle?: string;
  onTitleChange?: (title: string) => void;
  documentType?: DocumentType;
  isSaved?: boolean;
  className?: string;
  onOpenHistory?: () => void;
  onShare?: () => void;
  onDownloadSource?: () => void;
  onDownloadPdf?: () => void;
  onDownloadDocx?: () => void;
  onPrint?: () => void;
  onOpenShortcuts?: () => void;
  onWordCount?: () => void;
}

const TOPBAR_HEIGHT = 44;

const spring = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };

function BarButton({
  onClick,
  children,
  label,
  className,
  active,
}: {
  onClick?: () => void;
  children: React.ReactNode;
  label: string;
  className?: string;
  active?: boolean;
}) {
  return (
    <motion.button
      onClick={onClick}
      aria-label={label}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.92 }}
      transition={spring}
      className={cn(
        "flex items-center justify-center rounded-[var(--radius-sm)] p-1.5",
        "transition-colors duration-100",
        active
          ? "text-[var(--text-primary)] bg-[var(--bg-surface-hover)]"
          : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
        className,
      )}
    >
      {children}
    </motion.button>
  );
}

function EditableTitle({
  value,
  onChange,
}: {
  value: string;
  onChange?: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  const commit = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onChange?.(trimmed);
    else setDraft(value);
  }, [draft, value, onChange]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") commit();
      if (e.key === "Escape") { setDraft(value); setEditing(false); }
    },
    [commit, value],
  );

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        className={cn(
          "max-w-[260px] rounded-[var(--radius-sm)] border border-[var(--border-subtle)] bg-transparent",
          "px-1.5 py-0.5 text-[13px] font-medium text-[var(--text-primary)]",
          "outline-none focus:border-[var(--color-lever-orange)]",
        )}
        style={{ fontFamily: "var(--font-sans)" }}
      />
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className={cn(
        "max-w-[260px] truncate rounded-[var(--radius-sm)] px-1.5 py-0.5",
        "text-[13px] font-medium text-[var(--text-primary)]",
        "hover:bg-[var(--bg-surface-hover)]",
        "transition-colors duration-100 cursor-text",
      )}
      style={{ fontFamily: "var(--font-sans)" }}
    >
      {value}
    </button>
  );
}

function TypeBadge({ type }: { type: DocumentType }) {
  const label = type === "latex" ? "LaTeX" : type === "docx" ? "DOC" : "MD";
  return (
    <span
      className={cn(
        "rounded-[var(--radius-sm)] px-1.5 py-px",
        "text-[10px] font-semibold tracking-wider uppercase",
        "bg-[var(--bg-surface-hover)] text-[var(--text-muted)]",
        "border border-[var(--border-hairline)]",
      )}
    >
      {label}
    </span>
  );
}

function SaveIndicator({ saved }: { saved: boolean }) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={saved ? "saved" : "unsaved"}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.15 }}
        className="flex items-center gap-1"
      >
        {saved ? (
          <div className="flex items-center gap-1 text-[var(--text-muted)]">
            <Check size={10} weight="bold" />
            <span className="text-[10px]">Saved</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-[var(--color-ember-orange)]">
            <motion.div
              className="h-1.5 w-1.5 rounded-full bg-[var(--color-ember-orange)]"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
            <span className="text-[10px]">Unsaved</span>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

export function TopBar({
  documentTitle = "Untitled",
  onTitleChange,
  documentType = "latex",
  isSaved = true,
  className,
  onOpenHistory,
  onShare,
  onDownloadSource,
  onDownloadPdf,
  onDownloadDocx,
  onPrint,
  onOpenShortcuts,
  onWordCount,
}: TopBarProps) {
  const navigate = useNavigate();
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const focusMode = useUIStore((s) => s.focusMode);
  const toggleFocusMode = useUIStore((s) => s.toggleFocusMode);

  return (
    <header
      className={cn(
        "flex shrink-0 items-center justify-between border-b border-[var(--border-hairline)] px-2",
        "bg-[var(--bg-surface)] select-none",
        className,
      )}
      style={{ height: TOPBAR_HEIGHT }}
    >
      {/* Left: logo + sidebar toggle */}
      <div className="flex items-center gap-1">
        <motion.button
          onClick={() => navigate("/")}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          transition={spring}
          className="flex items-center gap-1.5 rounded-[var(--radius-sm)] px-1.5 py-1 hover:bg-[var(--bg-surface-hover)] transition-colors duration-100"
        >
          <div className="flex h-5 w-5 items-center justify-center text-[var(--text-primary)]">
            <LeverMark size={16} />
          </div>
          <span
            className="text-[13px] font-semibold tracking-[0.01em] text-[var(--text-primary)] hidden sm:inline"
            style={appNameStyle}
          >
            {APP_NAME}
          </span>
        </motion.button>

        <div className="mx-0.5 h-4 w-px bg-[var(--border-hairline)]" />

        <BarButton onClick={toggleSidebar} label="Toggle sidebar">
          <SidebarSimple size={15} />
        </BarButton>
      </div>

      {/* Center: title + badge + save status + agent */}
      <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 max-w-[50vw] overflow-hidden">
        <EditableTitle value={documentTitle} onChange={onTitleChange} />
        <TypeBadge type={documentType} />
        <SaveIndicator saved={isSaved} />
        <AgentStatusIndicator />
      </div>

      {/* Right: history, share, focus, menu */}
      <div className="flex shrink-0 items-center gap-0.5">
        {onOpenHistory && (
          <BarButton onClick={onOpenHistory} label="History">
            <ClockCounterClockwise size={15} />
          </BarButton>
        )}

        {onShare && (
          <motion.button
            onClick={onShare}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.94 }}
            transition={spring}
            className={cn(
              "flex items-center gap-1.5 rounded-full px-2.5 py-1 ml-0.5",
              "text-[11px] font-semibold",
              "bg-[var(--color-lever-orange)] text-white",
              "hover:brightness-110 transition-all duration-100",
            )}
          >
            <ShareNetwork size={12} weight="bold" />
            Share
          </motion.button>
        )}

        <ModelSelector />

        <div className="mx-0.5 h-4 w-px bg-[var(--border-hairline)]" />

        <BarButton onClick={toggleFocusMode} label="Toggle focus mode" active={focusMode}>
          {focusMode ? <EyeSlash size={15} /> : <Eye size={15} />}
        </BarButton>

        <Dropdown>
          <DropdownTrigger
            className={cn(
              "flex items-center justify-center rounded-[var(--radius-sm)] p-1.5",
              "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
              "transition-colors duration-100",
            )}
          >
            <DotsThreeVertical size={15} weight="bold" />
          </DropdownTrigger>
          <DropdownMenu align="end" className="w-[200px]">
            {onDownloadSource && (
              <DropdownItem icon={<FileText size={14} />} onClick={onDownloadSource}>
                Download Source
              </DropdownItem>
            )}
            {onDownloadPdf && (
              <DropdownItem icon={<DownloadSimple size={14} />} onClick={onDownloadPdf}>
                Download PDF
              </DropdownItem>
            )}
            {onDownloadDocx && (
              <DropdownItem icon={<DownloadSimple size={14} />} onClick={onDownloadDocx}>
                Export as .docx
              </DropdownItem>
            )}
            {onPrint && (
              <DropdownItem icon={<Printer size={14} />} onClick={onPrint}>
                Print
              </DropdownItem>
            )}
            {(onDownloadSource || onDownloadPdf || onDownloadDocx || onPrint) && <DropdownSeparator />}
            {onWordCount && (
              <DropdownItem icon={<TextAa size={14} />} onClick={onWordCount}>
                Word Count
              </DropdownItem>
            )}
            {onOpenShortcuts && (
              <DropdownItem icon={<Keyboard size={14} />} onClick={onOpenShortcuts}>
                Keyboard Shortcuts
              </DropdownItem>
            )}
            <DropdownSeparator />
            <DropdownItem icon={<Gear size={14} />} onClick={() => navigate("/settings")}>
              Settings
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
    </header>
  );
}

export { TOPBAR_HEIGHT };
